<template>
  <Modal fullbelowsm :title="!editData ? $t('projects.logging_providers.create') : $t('projects.logging_providers.edit')">
    <div class="w-600 flex flex-col">
      <div class="w-full flex flex-col justify-start mt-3 py-3 px-10 flex-shrink-0">
        <ValidationObserver ref="validationObserver" v-slot="{ errors }" class="w-full">
          <FormLabel :label="$t('projects.logging_providers.provider')" />
          <div class="font-500">{{ providerName }}</div>

          <FormSelect
            v-model="region"
            :options="regionOptions"
            default-reducer
            :label="$t('projects.logging_providers.region')"
            name="region"
            :error="errors.region"
            rules="required"
            class="mt-3"
            append-to-body
          />
          <div class="text-11 text-gray-500 mt-1">{{ $t('projects.logging_providers.region_help') }}</div>

          <FormInput v-model="key" type="password" :label="$t('projects.logging_providers.key')" name="key" :error="errors.key" rules="required" class="mt-3" />
          <div class="text-11 text-gray-500 mt-1">{{ $t('projects.logging_providers.key_help') }}</div>

          <div class="flex items-center justify-between mt-4">
            <FormLabel :label="$t('projects.logging_providers.enabled')" class="!mb-0" />
            <Toggle v-model="enabled" dense />
          </div>
          <div class="text-11 text-gray-500 mt-1">{{ $t('projects.logging_providers.enabled_help') }}</div>

          <FormSelect
            v-model="minLevel"
            :options="levelOptions"
            default-reducer
            :label="$t('projects.logging_providers.min_level')"
            name="min_level"
            :error="errors.min_level"
            rules="required"
            class="mt-4"
            append-to-body
          />
          <div class="text-11 text-gray-500 mt-1">{{ $t('projects.logging_providers.min_level_help') }}</div>

          <FormLabel :label="$t('projects.logging_providers.categories_title')" class="block mt-4" />
          <div class="flex flex-col gap-y-3 mt-1">
            <Checkbox v-for="category in categoryKeys" :key="category" v-model="categories[category]" :name="category">
              <div class="flex flex-col">
                <span class="text-13 font-500">{{ $t(`projects.logging_providers.categories.${category}.label`) }}</span>
                <span class="text-11 text-gray-500">{{ $t(`projects.logging_providers.categories.${category}.help`) }}</span>
              </div>
            </Checkbox>
          </div>

          <div class="text-11 text-gray-500 mt-4 p-3 rounded-8 bg-gray-50 border border-gray-100">{{ $t('projects.logging_providers.always_sent_note') }}</div>

          <FormError v-if="error" :label="error" class="block" />
        </ValidationObserver>
      </div>
    </div>

    <template #footer>
      <div class="w-full flex py-4 px-10">
        <PillButton v-if="editData" error :text="$t('delete')" :disabled="loading" @click="onDeleteClick" />
        <PillButton outlined :text="$t('cancel')" :disabled="loading" class="ml-auto" @click="onCancelClick" />
        <PillButton primary :loading="loading" :disabled="loading" class="ml-2" :text="$t('save')" outlined @click="onSaveClick" />
      </div>
    </template>
  </Modal>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import DeleteModal from '../modals/DeleteModal.vue';

export default {
  name: 'CreateLoggingProviderModal',
  props: {
    projectId: {
      type: String,
      required: true,
    },
    provider: {
      type: String,
    },
    providerName: {
      type: String,
    },
    editData: {
      type: Object,
    },
  },
  data() {
    return {
      loading: false,
      error: '',
      region: '',
      key: '',
      enabled: true,
      minLevel: '',
      categories: {},
    };
  },
  created() {
    const categoryDefaults = Object.fromEntries(Object.entries(this.loggingOptions?.categories || {}).map(([category, option]) => [category, option?.default === true]));
    this.region = this.availableLoggingProviders[this.provider]?.default_region || '';
    this.minLevel = this.loggingOptions?.min_level?.default || '';
    this.categories = categoryDefaults;
    if (this.editData) {
      const config = this.editData.config || {};
      this.region = config.region || this.region;
      this.key = config.key || '';
      this.enabled = config.enabled !== false;
      this.minLevel = config.options?.min_level || this.minLevel;
      this.categories = { ...categoryDefaults, ...(config.options?.categories || {}) };
    }
  },
  computed: {
    ...mapState(['availableLoggingProviders', 'loggingOptions']),
    /**
     * Region options offered by the selected provider.
     * @returns {Array<{label: string, value: string}>} Select options.
     */
    regionOptions() {
      return (this.availableLoggingProviders[this.provider]?.regions || []).map((region) => ({ label: region, value: region }));
    },
    /**
     * Minimum level options from the logging options.
     * @returns {Array<{label: string, value: string}>} Select options.
     */
    levelOptions() {
      return (this.loggingOptions?.min_level?.values || []).map((level) => ({ label: level, value: level }));
    },
    /**
     * Category keys shown as checkboxes, in the server's order.
     * @returns {string[]} Category keys.
     */
    categoryKeys() {
      return Object.keys(this.loggingOptions?.categories || {});
    },
  },
  methods: {
    ...mapActions(['saveProjectLoggingProvider', 'deleteProjectLoggingProvider']),
    /**
     * Validates and saves the provider; a rejected key or other failure is shown inline.
     * @returns {Promise<void>}
     */
    async onSaveClick() {
      const valid = await this.$refs.validationObserver.validate();
      if (!valid.valid) return;
      this.loading = true;
      this.error = '';
      const response = await this.saveProjectLoggingProvider({
        project_id: this.projectId,
        provider: this.provider,
        config: { region: this.region, key: this.key, enabled: this.enabled, options: { min_level: this.minLevel, categories: { ...this.categories } } },
      });
      this.loading = false;
      if (response.error) {
        this.error = response.error;
        return;
      }
      this.$emit('close');
    },
    /**
     * Asks for confirmation, then removes the provider; a failure is shown inline in this modal.
     * @returns {void}
     */
    onDeleteClick() {
      this.$showModal(DeleteModal, {
        subtitle: this.$t('projects.logging_providers.remove_provider', { provider: this.providerName }),
        info: null,
        onConfirm: async () => {
          this.error = '';
          const response = await this.deleteProjectLoggingProvider({ project_id: this.projectId, provider: this.provider });
          if (response.error) {
            this.error = response.error;
            return;
          }
          this.$emit('close');
        },
      });
    },
    /**
     * Closes the modal without saving.
     * @returns {void}
     */
    onCancelClick() {
      this.$emit('close');
    },
  },
};
</script>
